import { useEffect } from 'react';
import {
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';

type OpsReportingConfig = {
	isOperationReportsEnabled: boolean;
};

export const OPS_REPORTING_CONFIG_RESOURCE_KEY = 'OPS_REPORTING_CONFIG';

export const opsReportingConfigResource: RouteResource<OpsReportingConfig> =
	createResource<OpsReportingConfig>({
		type: OPS_REPORTING_CONFIG_RESOURCE_KEY,
		getKey: () => OPS_REPORTING_CONFIG_RESOURCE_KEY,
		getData: async (_, { tenantContext: { activationId: workspaceId } }) => {
			if (!fg('platform_avp_jira_ops_reporting')) {
				return { isOperationReportsEnabled: false };
			}

			const url =
				typeof window === 'undefined'
					? '/api/v1/config'
					: `/gateway/api/jsm/reporting/opsgenie/workspace/${workspaceId}/config`;

			return performGetRequest<OpsReportingConfig>(url, {
				headers: {
					'x-ssr-service-name': 'jsm-reporting',
					'atl-workspaceid': workspaceId,
					'atl-workspacetype': 'opsgenie',
				},
			});
		},
		maxAge: 1000 * 60 * 60 * 24, // 24 hours
	});

export const useOpsReportingConfig = (): UseResourceHookResponse<OpsReportingConfig> =>
	useResource(opsReportingConfigResource);

/**
 * Reads the ops reporting config, triggering a client-side fetch on mount when
 * the resource has not already been loaded by the route.
 *
 * Use this in the global Operations navigation, which renders on many routes
 * that do not (and should not) register `opsReportingConfigResource`. On the
 * reports-new route the resource is registered and prefetched, so the effect is
 * a no-op there and the SSR-loaded data is reused.
 */
export const useOpsReportingConfigWithLoadOnMount =
	(): UseResourceHookResponse<OpsReportingConfig> => {
		const resource = useResource(opsReportingConfigResource);
		const { data, loading, error, refresh } = resource;

		useEffect(() => {
			if (!data && !loading && !error) {
				refresh();
			}
		}, [data, loading, error, refresh]);

		return resource;
	};
